import posthog from "posthog-js";
import type { ParticipationCapture, ParticipationEventMap } from "~/components/participation/analytics";

export type AppDownloadDirection = "events" | "clubs" | "club_chats";

export type EventTypeProperty = "free" | "ticketed";

export type CheckoutStepProperty = "tickets" | "details" | "email" | "payment";

export type PaymentMethodProperty = "card" | "apple-pay" | "google-pay";

type CheckoutFunnelProps = {
  event_id: string;
  event_title?: string;
};

type EventMap = ParticipationEventMap & {
  app_download_clicked: {
    direction: AppDownloadDirection;
    surface: string;
    appsflyer_type?: string;
  };
  event_viewed: {
    event_id: string;
    event_title?: string;
    event_type: EventTypeProperty;
    is_authenticated: boolean;
    is_attending: boolean;
    at_capacity: boolean;
    has_club: boolean;
    has_invite_token: boolean;
  };
  checkout_started: CheckoutFunnelProps;
  checkout_details_submitted: CheckoutFunnelProps;
  checkout_payment_viewed: CheckoutFunnelProps;
  checkout_payment_succeeded: CheckoutFunnelProps;
  checkout_payment_failed: CheckoutFunnelProps & {
    error_type?: string;
    error_code?: string;
    payment_method: PaymentMethodProperty;
  };
  checkout_abandoned: CheckoutFunnelProps & {
    step: CheckoutStepProperty;
  };
  checkout_attendee_form_submitted: CheckoutFunnelProps;
  checkout_completed: CheckoutFunnelProps;
  discount_code_applied: CheckoutFunnelProps & {
    code: string;
  };
  discount_code_rejected: CheckoutFunnelProps & {
    code: string;
  };
};

export function track<E extends keyof EventMap>(
  event: E,
  properties: EventMap[E],
) {
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics is observational. It must not interrupt navigation or payment.
  }
}

// Participation payloads are already allowlisted by the transition adapter.
export const trackParticipation: ParticipationCapture = (event, properties) => {
  try { posthog.capture(event, properties); } catch { /* Best effort. */ }
};

// Compatibility boundary for the existing checkout funnel. These events keep
// their established meanings; the participation family never re-emits them.
type LegacyCheckoutEvent = Extract<keyof EventMap, `checkout_${string}` | `discount_code_${string}`>;
export function trackLegacyCheckout<E extends LegacyCheckoutEvent>(event: E, properties: EventMap[E]) {
  track(event, properties);
}
